import { createSlice } from '@reduxjs/toolkit';
import { TicketDetailState } from './ticketDetailTypes';
import {
    createTicketDetail,
    fetchTicketDetailById,
    fetchAllTicketDetails,
    updateTicketDetail,
    deleteTicketDetail,
    createCommentOrReply,
    fetchTicketDetailsWithReplies,
    updateTicketAssignee
} from './ticketDetailActions';

const initialState: TicketDetailState = {
    ticketDetails: [],
    ticketRepleisComments: [],
    selectedTicketDetail: undefined,
    loading: false,
    error: undefined,
};

const ticketDetailSlice = createSlice({
    name: 'ticketDetail',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Create Ticket Detail
            .addCase(createTicketDetail.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(createTicketDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.ticketDetails.push(action.payload);
            })
            .addCase(createTicketDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch Ticket Detail by ID
            .addCase(fetchTicketDetailById.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchTicketDetailById.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedTicketDetail = action.payload;
            })
            .addCase(fetchTicketDetailById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch All Ticket Details
            .addCase(fetchAllTicketDetails.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchAllTicketDetails.fulfilled, (state, action) => {
                state.loading = false;
                state.ticketDetails = action.payload.data;
            })
            .addCase(fetchAllTicketDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Update Ticket Detail
            .addCase(updateTicketDetail.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(updateTicketDetail.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.ticketDetails.findIndex(detail => detail.id === action.payload.id);
                if (index !== -1) {
                    state.ticketDetails[index] = action.payload;
                }
            })
            .addCase(updateTicketDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateTicketAssignee.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(updateTicketAssignee.fulfilled, (state, action) => {
                state.loading = false;
                const { ticketId, assignedTo } = action.payload;

                const index = state.ticketDetails.findIndex(detail => detail.id === ticketId);
                if (index !== -1) {
                    state.ticketDetails[index].AssignedTo = assignedTo;

                    // Optional: if selectedTicketDetail is loaded and matches, update it too
                    if (state.selectedTicketDetail?.id === ticketId) {
                        state.selectedTicketDetail.AssignedTo = assignedTo;
                    }
                }
            })
            .addCase(updateTicketAssignee.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Delete Ticket Detail
            .addCase(deleteTicketDetail.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(deleteTicketDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.ticketDetails = state.ticketDetails.filter(detail => detail.id !== action.payload.id);
            })
            .addCase(deleteTicketDetail.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Create Comment or Reply
            .addCase(createCommentOrReply.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(createCommentOrReply.fulfilled, (state, action) => {
                state.loading = false;
                state.ticketDetails.push(action.payload);
            })
            .addCase(createCommentOrReply.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch Ticket Details with Replies
            .addCase(fetchTicketDetailsWithReplies.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchTicketDetailsWithReplies.fulfilled, (state, action) => {
                state.loading = false;
                state.ticketRepleisComments = action.payload.data;
            })
            .addCase(fetchTicketDetailsWithReplies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export default ticketDetailSlice.reducer;
